// NPM Imports
import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// CSS Imports
import signIn from "./signin.module.scss";

// Component Imports
import GoogleAuth from "../GoogleAuth";

// Internal Imports
import { GlobalUtils } from "../../utils";

// Service Imports
import {
  googleSigninService,
  popDemoDocument,
  slackService,
} from "../../AuthService/authService";
import { redirectPostAuthentication } from "../../AuthService/authMethods";

// Destructuring
const { setUserCookies } = GlobalUtils;

function SignInGoogle(props) {
  const {
    data: { email, selectedDomain },
  } = props;

  // Local States
  const [state, setState] = useState({
    errMsg: "",
    googleBtnLoader: false,
  });

  // Local Functions

  const _onGoogleSuccess = response => {
    const { profileObj: { email: gEmail } = {} } = response;

    // checking if the both entered 'email' and
    // google authenticated 'gEmail' are same or not
    if (email !== (gEmail && gEmail.toLowerCase())) {
      setState(prev => ({
        ...prev,
        errMsg: "Emails mismatched. Please try again!",
      }));
    } else {
      const payload = {
        id_token: response.tokenObj.id_token,
        org_domain: selectedDomain,
      };
      setState(prev => ({ ...prev, googleBtnLoader: true }));
      googleSigninService(payload)
        .then(async res => {
          if (res && res.subdomain) {
            setUserCookies(res);
            // Pop the document if user tried in the document playground
            const slackAuthentication = slackService(res);
            const retainDemoDocument = popDemoDocument();
            await Promise.all([slackAuthentication, retainDemoDocument])
              .then(resp => {
                const docData = resp[1];
                redirectPostAuthentication(res, docData, {
                  flow: "signin",
                  params: { method: "gmail" },
                });
              })
              .catch(() => {
                redirectPostAuthentication(res, null, {
                  flow: "signin",
                  params: { method: "gmail" },
                });
              });
          } else {
            setState(prev => ({
              ...prev,
              googleBtnLoader: false,
              errMsg: "Something went wrong. Please try again!",
            }));
          }
        })
        .catch(err => {
          console.error(`SignInGoogle => _onSuccess => ${err}`);
          if (
            err.response &&
            (err.response.status === 401 || err.response.status === 400)
          ) {
            setState(prev => ({
              ...prev,
              googleBtnLoader: false,
              errMsg: "Invalid Signin Details",
            }));
          } else {
            setState(prev => ({
              ...prev,
              googleBtnLoader: false,
              errMsg: "Something went wrong. Please try again!",
            }));
          }
        });
    }

    // disconnecting(signout) the google sign
    const auth2 = window.gapi.auth2.getAuthInstance();
    if (auth2 != null) {
      auth2.signOut().then(auth2.disconnect());
    }
  };

  const _onGoogleFailure = googleError => {
    /**
     * Google Auth Button may fail in the following cases with the respective error codes:
     * 1. "idpiframe_initialization_failed" :=> Initialization of the Google Auth API failed
     *                                          (this will occur if a client doesn't have third party cookies enabled),
     * 2. "popup_closed_by_user"            :=> The user closed the popup before finishing the sign in flow,
     * 3. "access_denied"                   :=> The user denied the permission to the scopes required,
     * 4. "immediate_failed"                :=> No user could be automatically selected without prompting the consent flow.
     */

    if (
      googleError &&
      googleError.error === "idpiframe_initialization_failed"
    ) {
      setState(prev => ({
        ...prev,
        errMsg:
          "To sign in via Google email, please enable cookies in your browser settings.",
      }));
    } else {
      setState(prev => ({
        ...prev,
        errMsg: "Something went wrong. Please try again!",
      }));
    }
  };

  return (
    <Fragment>
      {/** HEADER SECTION */}
      {state.errMsg && (
        <div className={signIn.errorMessage}>
          <span className={signIn.errorMessageIcon}>
            <FontAwesomeIcon icon={["far", "times-circle"]} />
          </span>
          {state.errMsg}
        </div>
      )}

      {/** BODY SECTION */}
      <div className={signIn.googleBtn}>
        {state.googleBtnLoader && (
          <div className="signup-googleauth-btn-loader">
            <FontAwesomeIcon icon={["far", "spinner"]} className="fa-spin" />
          </div>
        )}
        <GoogleAuth
          btnTitle="Sign in with Google"
          onSuccess={res => _onGoogleSuccess(res)}
          onFailure={err => _onGoogleFailure(err)}
        />
      </div>
    </Fragment>
  );
}

SignInGoogle.defaultProps = {};

SignInGoogle.propTypes = {
  data: PropTypes.shape({
    email: PropTypes.string.isRequired,
    selectedDomain: PropTypes.string.isRequired,
  }).isRequired,
};

export default SignInGoogle;
